import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AmplitudeEventService, StorageManagerService } from '@core/services';
import { LoggedOut, RegistrationEmailValidated } from '@integrations/amplitude';
import { TranslateService } from '@ngx-translate/core';
import { AuthResponse, LicensePlate, LoginEmailResponse, LoginTokenResponse, VerifySignupJWTResponse } from '@shared/models';
import { ToastService } from '@shared/services';
import { showSpinner } from '@shared/utils';
import { Observable, catchError, finalize, map, of, switchMap, tap } from 'rxjs';

export type SignupSessionPayload = {
  termsAndConditions: boolean;
  marketingConsent: boolean;
  licensePlate: LicensePlate | null;
};

export enum SSOProvider {
  Google = 'google',
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private router: Router,
    private http: HttpClient,
    private storeManager: StorageManagerService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private amplitudeEventService: AmplitudeEventService,
  ) { }

  logIn(token: string, destination?: string) {
    this.storeManager.store('token', token);
    this.storeManager.store('authenticated', 'true');
    if (destination) {
      if (destination.startsWith('/onboarding/finish')) {
        this.amplitudeEventService.trackEvent(
          new RegistrationEmailValidated({
            registrationFlowType: 'ppu',
          }),
        );
        this.storeManager.store('userInitialLogin', 'true');
      }
      this.router.navigateByUrl(destination);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  logOut() {
    this.amplitudeEventService.trackEvent(new LoggedOut());
    this.http
      .delete(`/api/user/auth/app/v1/auth/session`, {})
      .pipe(showSpinner())
      .subscribe({
        next: () => {
          this.storeManager.delete('token');
          this.storeManager.delete('authenticated');
          this.storeManager.delete('userInitialLogin');
          this.router.navigate(['/login']);
        },
        error: () => {
          this.storeManager.delete('token');
          this.storeManager.delete('authenticated');
          this.storeManager.delete('userInitialLogin');
          this.router.navigate(['/login']);
        },
      });
  }

  logOutWithoutRedirect(): Observable<void> {
    // For activation/email-verification flows, force a local logout first.
    // These links are usually opened by users who should not reuse an existing session.
    // Even if backend session deletion fails, clear frontend auth state unconditionally.
    const clearAuthState = () => {
      this.storeManager.delete('token');
      this.storeManager.delete('authenticated');
      this.storeManager.delete('userInitialLogin');
    };

    const tokenKey = 'token';
    const currentToken = this.storeManager.load(tokenKey);
    const localStorageToken = this.storeManager.loadFromLocalStorage(tokenKey);

    const logoutWithLocalToken$ =
      currentToken !== localStorageToken && !!localStorageToken
        ? this.http
            .delete(`/api/user/auth/app/v1/auth/session`, {
              headers: { Authorization: `Token ${localStorageToken}` },
            })
            .pipe(
              map(() => undefined),
              catchError(() => of(undefined)),
            )
        : of(undefined);

    return logoutWithLocalToken$.pipe(
      switchMap(() =>
        this.http.delete(
          `/api/user/auth/app/v1/auth/session`,
          currentToken ? { headers: { Authorization: `Token ${currentToken}` } } : {}, 
        ).pipe(
          tap(() => {
            clearAuthState();
          }),
          map(() => undefined),
          catchError(() => {
            clearAuthState();
            return of(undefined);
          }),
        ),
      ),
      finalize(clearAuthState),
    );
  }

  verifyLoginJWT(token: string): Observable<VerifySignupJWTResponse> {
    return this.http.post<VerifySignupJWTResponse>(`/api/user/auth/app/v1/auth/code/confirm`, {
      code: token,
    });
  }

  emailVerifyJWT(token: string): Observable<VerifySignupJWTResponse> {
    return this.http.post<VerifySignupJWTResponse>(`/api/user/auth/app/v1/auth/email/verify`, {
      key: token,
    });
  }

  loginDeprecatedJWT(token: string): Observable<LoginTokenResponse> {
    // DEPRECATED - only needed to not break old login links until they are expired, can be removed after some time
    return this.http.post<LoginTokenResponse>(`/api/user/v2/user/welcome_email_from_token/`, {
      token: token,
    });
  }

  verifySignupJWT(token: string): Observable<VerifySignupJWTResponse> {
    return this.http.post<VerifySignupJWTResponse>(`/api/user/auth/app/v1/auth/signup/`, {
      token,
    });
  }

  activateAccount(token: string): Observable<LoginTokenResponse> {
    return this.http.post<LoginTokenResponse>(`/api/user/v2/auth/register/activate`, {
      token,
    });
  }

  checkAuthStatus(): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`/api/user/auth/browser/confirm_login`, {});
  }

  showProviderSignupErrorToast(message?: string): void {
    this.toastService.add(
      {
        message: this.translateService.instant(message ?? "SignUp.ProviderSignupError"),
        type: 'error',
      },
      10000,
    );
  }

  showProviderSignupPartialSuccess(): void {
    this.toastService.add(
      {
        message: this.translateService.instant("SignUp.ProviderSignupPartialSuccess"),
        type: 'success',
      },
      10000,
    );
  }

  showTokenVerifyErrorToast(error: HttpErrorResponse, message? : string) : void {
    if (!message) {
      if (error.status === 401) {
        if (error.error?.error_code === 'invalid_token' || error.error?.error_code === 'token_invalid') {
          message = 'Common.Errors.TokenLinkInvalid';
        } else if (error.error?.error_code === 'token_expired') {
          message = 'Common.Errors.TokenLinkExpired';
        } else if (error.error?.error_code === 'token_not_found') {
          message = 'Common.Errors.TokenLinkAlreadyUsed';
        }
      }
      if (error.status === 400) {
        if (error.error?.error_code === 'license_plate_used') {
          message = 'Common.Errors.TokenLicensePlateAlreadyUsed';
        }
        if (error.error?.error_code === 'email_used') {
          message = 'Common.Errors.TokenEmailAlreadyUsed';
        }
        if (error.error?.error_code === 'incorrect_code') {
          message = 'Common.Errors.IncorrectCode';
        }
      }
    }
    this.toastService.add(
      {
        message: this.translateService.instant(message ?? 'Common.Errors.UnknownError'),
        type: 'error',
      },
      10000,
    );
  }

  authenticateSSOProviderSignup(provider: SSOProvider): void {
    const action = `${window.location.origin}/api/user/auth/browser/v1/auth/provider/redirect`;
    const form = document.createElement('form');
    form.method = 'post';
    form.action = action;
    form.style.display = 'none';

    const params: Record<string, string> = {
      provider: provider,
      process: 'login',
      callback_url: `${window.location.origin}/onboarding/signup`,
    };
    for (const [key, value] of Object.entries(params)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();

  }

  signUpWithSSOProvider(
    payload: SignupSessionPayload,
  ): Observable<VerifySignupJWTResponse> {
    return this.http.post<VerifySignupJWTResponse>(`/api/user/auth/browser/v1/auth/provider/signup`, {
      "terms_agreed": payload.termsAndConditions,
      "license_plate": payload.licensePlate?.license_plate ?? '',
      "license_plate_country": payload.licensePlate?.license_plate_country ?? '',
    });
  }

  logInWithGoogle(): void {
    const action = `${window.location.origin}/api/user/auth/browser/v1/auth/provider/redirect`;
    const form = document.createElement('form');
    form.method = 'post';
    form.action = action;
    form.style.display = 'none';

    const params: Record<string, string> = {
      provider: 'google',
      process: 'login',
      callback_url: `${window.location.origin}/login?checkAuthStatus=true`,
    };
    for (const [key, value] of Object.entries(params)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  }

  isEmailVerificationPending(error: LoginEmailResponse): boolean {
    return error?.data?.flows?.some((flow) => flow.id === 'verify_email' && flow.is_pending === true);
  }

  isLoginByCodeVerificationPending(error: LoginEmailResponse): boolean {
    return error?.data?.flows?.some((flow) => flow.id === 'login_by_code' && flow.is_pending === true);
  }

  isTermsAgreedRequiredError(error: HttpErrorResponse): boolean {
    const responseErrors = error.error?.errors;
    if (!Array.isArray(responseErrors)) {
      return false;
    }

    return responseErrors.some((entry: { code?: string; param?: string }) => {
      return entry.code === 'required' && entry.param === 'terms_agreed';
    });
  }

  isGoogleSignupPending(error: LoginEmailResponse): boolean {
    return error?.data?.flows?.some((flow) => flow.id === 'provider_signup' && flow.is_pending === true && flow.provider?.name === 'Google');
  }
}
